import { getSupabase } from '../lib/client';

export interface UserSettings {
  user_id: string;
  save_chat_history: boolean;
  share_analytics: boolean;
  enable_memory: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserSettingsUpdate {
  save_chat_history?: boolean;
  share_analytics?: boolean;
  enable_memory?: boolean;
}

const DEFAULT_SETTINGS: Omit<
  UserSettings,
  'user_id' | 'created_at' | 'updated_at'
> = {
  save_chat_history: true,
  share_analytics: true,
  enable_memory: true,
};

/**
 * Get user settings with lazy upsert.
 * If settings don't exist, creates them with defaults and returns.
 */
export const getUserSettings = async (
  userId: string
): Promise<UserSettings> => {
  const supabase = getSupabase();

  // Try to get existing settings
  // Note: user_settings table is created by migration but not in generated types yet
  // Using 'as never' pattern for type safety until types are regenerated
  const { data: existing, error: selectError } = (await supabase
    .from('user_settings' as never)
    .select('*')
    .eq('user_id', userId)
    .single()) as {
    data: UserSettings | null;
    error: { code?: string; message?: string } | null;
  };

  if (existing && !selectError) {
    return existing;
  }

  // If not found (PGRST116 = no rows), create with defaults
  if (selectError && selectError.code === 'PGRST116') {
    const { data: inserted, error: insertError } = (await supabase
      .from('user_settings' as never)
      .insert({ user_id: userId, ...DEFAULT_SETTINGS } as never)
      .select()
      .single()) as {
      data: UserSettings | null;
      error: { code?: string; message?: string } | null;
    };

    if (insertError) {
      // Handle race condition: another request might have inserted
      if (insertError.code === '23505') {
        // Unique violation - row was just created, fetch it
        const { data: refetched, error: refetchError } = (await supabase
          .from('user_settings' as never)
          .select('*')
          .eq('user_id', userId)
          .single()) as {
          data: UserSettings | null;
          error: { code?: string; message?: string } | null;
        };

        if (refetchError) throw new Error(refetchError.message);
        if (!refetched)
          throw new Error('Failed to fetch user settings after race condition');
        return refetched;
      }
      throw new Error(insertError.message);
    }

    if (!inserted) throw new Error('Failed to create user settings');
    return inserted;
  }

  // Some other error occurred
  if (selectError) throw new Error(selectError.message);

  // Fallback - should not reach here
  throw new Error('Unexpected state in getUserSettings');
};

/**
 * Update user settings
 */
export const updateUserSettings = async (
  userId: string,
  settings: UserSettingsUpdate
): Promise<UserSettings> => {
  const supabase = getSupabase();

  // Ensure settings exist first (lazy creation)
  await getUserSettings(userId);

  const { data, error } = (await supabase
    .from('user_settings' as never)
    .update(settings as never)
    .eq('user_id', userId)
    .select()
    .single()) as {
    data: UserSettings | null;
    error: { code?: string; message?: string } | null;
  };

  if (error) throw new Error(error.message);
  if (!data) throw new Error('Failed to update user settings');
  return data;
};
